/**
 * Invito di un account (V50): email, nome, cognome e almeno un ruolo, Mister
 * o Dirigente su una squadra-stagione per riga.
 *
 * Il server crea account e ruoli e invia l'email nella stessa operazione: se
 * l'email non parte non resta nulla, e il messaggio lo dice. Le regole sui
 * ruoli sono quelle di V51; qui si evita solo di proporre due volte la stessa
 * squadra-stagione.
 */

import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OctagonXIcon, PlusIcon, XIcon } from 'lucide-react';
import { invitaAccountSchema } from '@calcio/shared';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { useIndietro } from '../navigazione/indietro.js';
import { erroriPerCampo, messaggio } from '../comuni/dati.js';
import { type SquadraStagione, useContesto } from '../navigazione/contesto.js';
import { type RuoloAssegnabile, nomeSquadra, squadreLibereInvito } from './ruoli.js';
import { Nota } from '../comuni/Nota.js';

interface Riga {
  ruolo: RuoloAssegnabile;
  squadraStagioneId: string;
}

const rigaVuota = (): Riga => ({ ruolo: 'mister', squadraStagioneId: '' });

export function InvitaAccount() {
  const { squadre } = useContesto();
  const navigate = useNavigate();
  const annulla = useIndietro('/amministrazione/account');
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [righe, setRighe] = useState<Riga[]>([rigaVuota()]);
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const elenco = squadre ?? [];
  const scelte = righe.map((r) => r.squadraStagioneId);
  const cambiaRiga = (i: number, modifica: Partial<Riga>) =>
    setRighe((attuali) => attuali.map((r, j) => (j === i ? { ...r, ...modifica } : r)));

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = invitaAccountSchema.safeParse({ email, nome, cognome, ruoli: righe });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama('/amministrazione/account', { metodo: 'POST', corpo: esito.data });
      toast.success(`Invito inviato a ${esito.data.email}.`);
      navigate('/amministrazione/account', { replace: true });
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  return (
    <form onSubmit={(e) => void invia(e)}>
      <Testata titolo="Invita un account" indietro="/">
        <Nota>
          Arriva un'email con il link per scegliere la password, valido 7 giorni. L'Admin Squadra
          non si invita da qui: lo nomina il gestore della piattaforma.
        </Nota>
      </Testata>
      <Corpo className="flex max-w-2xl flex-col gap-8">
        <FieldGroup>
          <Field data-invalid={Boolean(errori.email)}>
            <FieldLabel htmlFor="invito-email">Email</FieldLabel>
            <Input
              id="invito-email"
              type="email"
              autoComplete="off"
              value={email}
              aria-invalid={Boolean(errori.email)}
              onChange={(e) => setEmail(e.target.value)}
            />
            <FieldError>{errori.email}</FieldError>
          </Field>
          <div className="grid gap-4 @lg:grid-cols-2">
            <Field data-invalid={Boolean(errori.nome)}>
              <FieldLabel htmlFor="invito-nome">Nome</FieldLabel>
              <Input
                id="invito-nome"
                value={nome}
                aria-invalid={Boolean(errori.nome)}
                onChange={(e) => setNome(e.target.value)}
              />
              <FieldError>{errori.nome}</FieldError>
            </Field>
            <Field data-invalid={Boolean(errori.cognome)}>
              <FieldLabel htmlFor="invito-cognome">Cognome</FieldLabel>
              <Input
                id="invito-cognome"
                value={cognome}
                aria-invalid={Boolean(errori.cognome)}
                onChange={(e) => setCognome(e.target.value)}
              />
              <FieldError>{errori.cognome}</FieldError>
            </Field>
          </div>
        </FieldGroup>

        <FieldSet>
          <FieldLegend>Ruoli</FieldLegend>
          <FieldDescription>
            Almeno uno. Il Mister gestisce attività, partite e valutazioni; il Dirigente fa lo
            stesso senza voti e attributi.
          </FieldDescription>
          <FieldGroup>
            {righe.map((riga, i) => (
              <RigaRuolo
                key={i}
                indice={i}
                riga={riga}
                squadre={squadreLibereInvito(scelte, i, elenco)}
                rimovibile={righe.length > 1}
                onCambia={(modifica) => cambiaRiga(i, modifica)}
                onRimuovi={() => setRighe((attuali) => attuali.filter((_, j) => j !== i))}
              />
            ))}
            <FieldError>{errori.ruoli}</FieldError>
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={righe.length >= elenco.length}
                onClick={() => setRighe((attuali) => [...attuali, rigaVuota()])}
              >
                <PlusIcon data-icon="inline-start" />
                Aggiungi un ruolo
              </Button>
            </div>
          </FieldGroup>
        </FieldSet>

        {erroreServer && (
          <Alert variant="destructive">
            <OctagonXIcon />
            <AlertTitle>Salvataggio non riuscito</AlertTitle>
            <AlertDescription>{erroreServer}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={annulla}>
            Annulla
          </Button>
          <PulsanteInvio type="submit" inCorso={inCorso}>
            Invia invito
          </PulsanteInvio>
        </div>
      </Corpo>
    </form>
  );
}

function RigaRuolo({
  indice,
  riga,
  squadre,
  rimovibile,
  onCambia,
  onRimuovi,
}: {
  indice: number;
  riga: Riga;
  squadre: SquadraStagione[];
  rimovibile: boolean;
  onCambia: (modifica: Partial<Riga>) => void;
  onRimuovi: () => void;
}) {
  // Stagione corrente in testa, come nel selettore: l'elenco arriva gia ordinato.
  const perStagione = new Map<string, SquadraStagione[]>();
  for (const s of squadre) {
    perStagione.set(s.stagione, [...(perStagione.get(s.stagione) ?? []), s]);
  }

  return (
    <div className="flex flex-wrap items-end gap-3">
      <Field className="w-auto">
        <FieldLabel className="sr-only">Ruolo {indice + 1}</FieldLabel>
        <ToggleGroup
          type="single"
          variant="outline"
          value={riga.ruolo}
          onValueChange={(v) => v && onCambia({ ruolo: v as RuoloAssegnabile })}
        >
          <ToggleGroupItem value="mister">Mister</ToggleGroupItem>
          <ToggleGroupItem value="dirigente">Dirigente</ToggleGroupItem>
        </ToggleGroup>
      </Field>
      <Field className="min-w-48 flex-1">
        <FieldLabel htmlFor={`invito-squadra-${indice}`} className="sr-only">
          Squadra-stagione del ruolo {indice + 1}
        </FieldLabel>
        <Select
          value={riga.squadraStagioneId}
          onValueChange={(v) => onCambia({ squadraStagioneId: v })}
        >
          <SelectTrigger id={`invito-squadra-${indice}`} className="w-full">
            <SelectValue placeholder="Scegli la squadra-stagione…" />
          </SelectTrigger>
          <SelectContent>
            {[...perStagione].map(([stagione, elenco]) => (
              <SelectGroup key={stagione}>
                <SelectLabel>{stagione}</SelectLabel>
                {elenco.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {nomeSquadra(s)}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {rimovibile && (
        <Button type="button" variant="ghost" size="icon" onClick={onRimuovi}>
          <XIcon />
          <span className="sr-only">Togli il ruolo {indice + 1}</span>
        </Button>
      )}
    </div>
  );
}
